import { useSelector } from 'react-redux';
import { createTheme, alpha } from '@mui/material/styles';
import { RootState } from '../store';

// Google Material Design 3 inspired color tokens
const getDesignTokens = (mode: 'light' | 'dark') => {
  const isLight = mode === 'light';

  return {
    // Primary colors - Navy/Purple (Fresha-inspired professional palette)
    primary: {
      main: isLight ? '#5B4FCC' : '#6B5AE0',
      light: isLight ? '#7C6FDD' : '#8B7AFF',
      dark: isLight ? '#4A32D8' : '#4A32D8',
      contrastText: '#ffffff',
    },
    // Secondary colors - Coral Pink (warmth & approachability)
    secondary: {
      main: isLight ? '#FF6B9D' : '#FFB3D1',
      light: isLight ? '#FFB3D1' : '#FFE6F0',
      dark: isLight ? '#E8407A' : '#FF8DB7',
      contrastText: '#ffffff',
    },
    // Tertiary colors - Teal (trust & innovation)
    tertiary: {
      main: isLight ? '#00BFA5' : '#5CDDC4',
      light: isLight ? '#5CDDC4' : '#B2ECE4',
      dark: isLight ? '#008E76' : '#26CAB5',
      contrastText: '#ffffff',
    },
    // Success colors - Fresh green
    success: {
      main: isLight ? '#10B981' : '#6EE7B7',
      light: isLight ? '#6EE7B7' : '#A7F3D0',
      dark: isLight ? '#059669' : '#34D399',
      contrastText: '#ffffff',
    },
    // Warning colors - Amber
    warning: {
      main: isLight ? '#F59E0B' : '#FCD34D',
      light: isLight ? '#FCD34D' : '#FDE68A',
      dark: isLight ? '#D97706' : '#FBBF24',
      contrastText: isLight ? '#1A1C2E' : '#1A1C2E',
    },
    // Error colors - Vibrant red
    error: {
      main: isLight ? '#EF4444' : '#FCA5A5',
      light: isLight ? '#FCA5A5' : '#FECACA',
      dark: isLight ? '#DC2626' : '#F87171',
      contrastText: '#ffffff',
    },
    // Info colors - Clear blue
    info: {
      main: isLight ? '#3B82F6' : '#93C5FD',
      light: isLight ? '#93C5FD' : '#DBEAFE',
      dark: isLight ? '#1D4ED8' : '#60A5FA',
      contrastText: '#ffffff',
    },
    // Background colors - Subtle grey-blue (Fresha approach)
    background: {
      default: isLight ? '#FAFBFC' : '#0F0F1E',
      paper: isLight ? '#FFFFFF' : '#1A1A2E',
    },
    // Surface colors - White dominates
    surface: {
      main: isLight ? '#FFFFFF' : '#1A1A2E',
      variant: isLight ? '#F5F7FA' : '#25253A',
      elevated: isLight ? '#FFFFFF' : '#2A2A40',
    },
    // Text colors - Navy-black instead of pure grey
    text: {
      primary: isLight ? '#1A1C2E' : '#F0F0F5',
      secondary: isLight ? '#6B7280' : '#A0A0B0',
      disabled: isLight ? '#9CA3AF' : '#6B7280',
    },
    // Divider - Very subtle
    divider: isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)',
    // Action states - Purple tints
    action: {
      active: isLight ? '#6B7280' : '#F0F0F5',
      hover: isLight ? 'rgba(91, 79, 204, 0.04)' : 'rgba(107, 90, 224, 0.08)',
      selected: isLight ? 'rgba(91, 79, 204, 0.12)' : 'rgba(107, 90, 224, 0.16)',
      disabled: isLight ? 'rgba(0, 0, 0, 0.38)' : 'rgba(255, 255, 255, 0.38)',
      disabledBackground: isLight ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)',
      focus: isLight ? 'rgba(91, 79, 204, 0.12)' : 'rgba(107, 90, 224, 0.16)',
    },
  };
};

export const useAppTheme = () => {
  const { mode } = useSelector((state: RootState) => state.theme);
  const tokens = getDesignTokens(mode);

  return createTheme({
    palette: {
      mode,
      ...tokens,
    },
    shape: {
      borderRadius: 8,
    },
    shadows: [
      'none',
      // MD3 elevation levels - Purple-tinted for brand consistency
      '0px 1px 3px rgba(91, 79, 204, 0.08), 0px 1px 2px rgba(91, 79, 204, 0.06)',
      '0px 3px 8px rgba(91, 79, 204, 0.10), 0px 2px 4px rgba(91, 79, 204, 0.06)',
      '0px 6px 16px rgba(91, 79, 204, 0.12), 0px 3px 6px rgba(91, 79, 204, 0.08)',
      '0px 10px 30px rgba(91, 79, 204, 0.15), 0px 5px 12px rgba(91, 79, 204, 0.10)',
      '0px 16px 48px rgba(91, 79, 204, 0.18), 0px 8px 16px rgba(91, 79, 204, 0.12)',
      // Keep remaining shadows for compatibility
      '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
      '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
      '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
      '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
      '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
      '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
      '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
      '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
      '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
      '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
      '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
      '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
      '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
      '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
      '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
      '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
      '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
      '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
      '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)',
    ],
    typography: {
      fontFamily: "'Open Sans', 'Roboto', 'Inter', 'Helvetica Neue', Arial, sans-serif",
      h1: {
        fontSize: '2.25rem',
        fontWeight: 600,
        letterSpacing: '-0.02em',
        lineHeight: 1.2,
      },
      h2: {
        fontSize: '1.75rem',
        fontWeight: 600,
        letterSpacing: '-0.015em',
        lineHeight: 1.3,
      },
      h3: {
        fontSize: '1.5rem',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        lineHeight: 1.3,
      },
      h4: {
        fontSize: '1.25rem',
        fontWeight: 600,
        letterSpacing: '0em',
        lineHeight: 1.4,
      },
      h5: {
        fontSize: '1.05rem',
        fontWeight: 600,
        letterSpacing: '0em',
        lineHeight: 1.4,
      },
      h6: {
        fontSize: '0.95rem',
        fontWeight: 600,
        letterSpacing: '0.0075em',
        lineHeight: 1.5,
      },
      subtitle1: {
        fontSize: '0.95rem',
        fontWeight: 500,
        letterSpacing: '0.00938em',
        lineHeight: 1.6, // Up from 1.5
      },
      subtitle2: {
        fontSize: '0.83rem',
        fontWeight: 500,
        letterSpacing: '0.00714em',
        lineHeight: 1.6, // Up from 1.57
      },
      body1: {
        fontSize: '0.95rem',
        fontWeight: 400,
        letterSpacing: '0em',
        lineHeight: 1.6, // Up from 1.5
      },
      body2: {
        fontSize: '0.82rem',
        fontWeight: 400,
        letterSpacing: '0.01em',
        lineHeight: 1.6, // Up from 1.57
      },
      button: {
        fontSize: '0.9rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        textTransform: 'none' as const,
      },
      caption: {
        fontSize: '0.75rem',
        fontWeight: 400,
        letterSpacing: '0.03333em',
        lineHeight: 1.66,
      },
      overline: {
        fontSize: '0.625rem',
        fontWeight: 500,
        letterSpacing: '0.08333em',
        textTransform: 'uppercase' as const,
        lineHeight: 2.66,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: mode === 'light' ? '#dadce0' : '#5f6368',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: mode === 'light' ? '#bdc1c6' : '#80868b',
            },
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: 12, // Reduced border radius
            border: 'none',
            transition: 'box-shadow 280ms cubic-bezier(0.4, 0, 0.2, 1)',
            backgroundColor: mode === 'light' ? tokens.surface.main : tokens.surface.elevated,
          },
          outlined: {
            border: `1px solid ${tokens.divider}`,
          },
          elevation0: {
            boxShadow: 'none',
          },
          elevation1: {
            boxShadow:
              mode === 'light'
                ? '0px 1px 3px rgba(91, 79, 204, 0.08), 0px 1px 2px rgba(91, 79, 204, 0.06)'
                : '0px 8px 24px rgba(0,0,0,0.35)',
          },
          elevation2: {
            boxShadow:
              mode === 'light'
                ? '0px 3px 8px rgba(91, 79, 204, 0.10), 0px 2px 4px rgba(91, 79, 204, 0.06)'
                : '0px 14px 40px rgba(0,0,0,0.45)',
          },
          elevation3: {
            boxShadow:
              mode === 'light'
                ? '0px 6px 16px rgba(91, 79, 204, 0.12), 0px 3px 6px rgba(91, 79, 204, 0.08)'
                : '0px 18px 60px rgba(0,0,0,0.5)',
          },
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 1,
        },
        styleOverrides: {
          root: {
            borderRadius: 12, // Reduced border radius
            border: 'none',
            boxShadow: '0px 1px 3px rgba(91, 79, 204, 0.08), 0px 1px 2px rgba(91, 79, 204, 0.06)',
            transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              boxShadow: '0px 3px 8px rgba(91, 79, 204, 0.10), 0px 2px 4px rgba(91, 79, 204, 0.06)',
              transform: 'translateY(-2px)', // Subtle lift
            },
          },
        },
      },
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: tokens.background.paper,
            borderBottom: `1px solid ${tokens.divider}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            backgroundColor: tokens.background.paper,
            borderRight: 'none',
            boxShadow: 'none',
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: false, // Enable subtle shadows
        },
        styleOverrides: {
          root: {
            borderRadius: 10, // Reduced for less roundness
            textTransform: 'none',
            fontWeight: 600, // Up from 500
            fontSize: '0.9rem',
            padding: '10px 28px',
            minHeight: 40,
            transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: '0px 1px 3px rgba(91, 79, 204, 0.08), 0px 1px 2px rgba(91, 79, 204, 0.06)',
          },
          contained: {
            '&:hover': {
              boxShadow: '0px 3px 8px rgba(91, 79, 204, 0.10), 0px 2px 4px rgba(91, 79, 204, 0.06)',
              transform: 'translateY(-1px)', // Subtle lift
            },
          },
          containedPrimary: {
            backgroundColor: tokens.primary.main,
            color: tokens.primary.contrastText,
            '&:hover': {
              backgroundColor: tokens.primary.dark,
            },
          },
          outlined: {
            borderWidth: '1.5px', // Thicker border
            borderColor: '#E5E7EB',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: tokens.action.hover,
              borderColor: tokens.primary.main,
              borderWidth: '1.5px',
            },
          },
          outlinedPrimary: {
            borderWidth: '1.5px',
            borderColor: tokens.primary.main,
            '&:hover': {
              backgroundColor: alpha(tokens.primary.main, 0.08),
              borderWidth: '1.5px',
            },
          },
          text: {
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: tokens.action.hover,
            },
          },
          textPrimary: {
            '&:hover': {
              backgroundColor: alpha(tokens.primary.main, 0.08),
            },
          },
          sizeSmall: {
            padding: '7px 18px',
            fontSize: '0.82rem',
            minHeight: 34,
            borderRadius: 10,
          },
          sizeLarge: {
            padding: '12px 32px',
            fontSize: '0.95rem',
            minHeight: 46,
            borderRadius: 12,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: tokens.action.hover,
            },
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            borderRadius: 14, // Reduced
            boxShadow: '0px 6px 16px rgba(91, 79, 204, 0.12), 0px 3px 6px rgba(91, 79, 204, 0.08)',
            '&:hover': {
              boxShadow:
                '0px 10px 30px rgba(91, 79, 204, 0.15), 0px 5px 12px rgba(91, 79, 204, 0.10)',
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
        },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 10, // Up from 8px (softer)
              backgroundColor: mode === 'light' ? '#FAFBFC' : tokens.surface.variant,
              transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
              '& fieldset': {
                borderColor: mode === 'light' ? '#E5E7EB' : 'rgba(255, 255, 255, 0.14)',
                borderWidth: 1.5, // Thicker
                transition: 'border-color 200ms cubic-bezier(0.4, 0, 0.2, 1)',
              },
              '&:hover fieldset': {
                borderColor: mode === 'light' ? '#9CA3AF' : 'rgba(255, 255, 255, 0.4)',
              },
              '&.Mui-focused': {
                backgroundColor: mode === 'light' ? '#FFFFFF' : tokens.surface.elevated,
              },
              '&.Mui-focused fieldset': {
                borderColor: tokens.primary.main,
                borderWidth: 2,
              },
            },
            '& .MuiInputLabel-root': {
              color: tokens.text.secondary,
              '&.Mui-focused': {
                color: tokens.primary.main,
              },
            },
            '& .MuiInputBase-input': {
              color: tokens.text.primary,
              padding: '12px 14px',
              fontSize: '0.9rem',
              '&::placeholder': {
                color: tokens.text.secondary,
                opacity: 0.7,
              },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            '& fieldset': {
              borderColor: mode === 'light' ? tokens.divider : 'rgba(255, 255, 255, 0.23)',
              borderWidth: 1,
            },
            '&:hover fieldset': {
              borderColor: mode === 'light' ? tokens.text.secondary : 'rgba(255, 255, 255, 0.4)',
            },
            '&.Mui-focused fieldset': {
              borderColor: tokens.primary.main,
              borderWidth: 2,
            },
          },
          input: {
            color: tokens.text.primary,
            '&::placeholder': {
              color: tokens.text.secondary,
              opacity: 0.7,
            },
          },
          notchedOutline: {
            borderColor: mode === 'light' ? tokens.divider : 'rgba(255, 255, 255, 0.23)',
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: tokens.text.secondary,
            '&.Mui-focused': {
              color: tokens.primary.main,
            },
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.09)',
            '&:hover': {
              backgroundColor:
                mode === 'light' ? 'rgba(0, 0, 0, 0.09)' : 'rgba(255, 255, 255, 0.13)',
            },
            '&.Mui-focused': {
              backgroundColor:
                mode === 'light' ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.09)',
            },
            '&:before': {
              borderBottomColor: mode === 'light' ? tokens.divider : 'rgba(255, 255, 255, 0.23)',
            },
            '&:after': {
              borderBottomColor: tokens.primary.main,
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            backgroundColor: 'transparent',
          },
          icon: {
            color: tokens.text.secondary,
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: mode === 'light' ? tokens.divider : 'rgba(255, 255, 255, 0.23)',
              },
              '&:hover fieldset': {
                borderColor: mode === 'light' ? tokens.text.secondary : 'rgba(255, 255, 255, 0.4)',
              },
              '&.Mui-focused fieldset': {
                borderColor: tokens.primary.main,
              },
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: tokens.text.secondary,
            '&.Mui-focused': {
              color: tokens.primary.main,
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            color: tokens.text.secondary,
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            borderRadius: 8, // Reduced
            boxShadow: '0px 6px 16px rgba(91, 79, 204, 0.12), 0px 3px 6px rgba(91, 79, 204, 0.08)',
          },
          listbox: {
            padding: '8px 0',
          },
          option: {
            borderRadius: 4,
            margin: '2px 8px',
            padding: '8px 12px',
            '&:hover': {
              backgroundColor: tokens.action.hover,
            },
            '&[aria-selected="true"]': {
              backgroundColor: alpha(tokens.primary.main, 0.12),
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 16, // Up from 8px (more pill-like)
            fontWeight: 600, // Up from 500 (bolder)
            fontSize: '0.8125rem',
            height: 32,
          },
          filled: {
            '&.MuiChip-colorDefault': {
              backgroundColor: mode === 'light' ? '#EEF2FE' : '#3c4043',
              color: mode === 'light' ? tokens.primary.main : '#ffffff',
            },
          },
          outlined: {
            borderColor: tokens.divider,
          },
          colorPrimary: {
            backgroundColor: alpha(tokens.primary.main, 0.12),
            color: tokens.primary.main,
          },
          colorSecondary: {
            backgroundColor: alpha(tokens.secondary.main, 0.12),
            color: tokens.secondary.main,
          },
          colorSuccess: {
            backgroundColor: alpha(tokens.success.main, 0.12),
            color: tokens.success.main,
          },
          colorError: {
            backgroundColor: alpha(tokens.error.main, 0.12),
            color: tokens.error.main,
          },
          colorWarning: {
            backgroundColor: alpha(tokens.warning.main, 0.12),
            color: tokens.warning.main,
          },
          colorInfo: {
            backgroundColor: alpha(tokens.info.main, 0.12),
            color: tokens.info.main,
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 42,
            height: 26,
            padding: 0,
            marginRight: 8,
            '& .MuiSwitch-switchBase': {
              padding: 0,
              margin: 3,
              transitionDuration: '300ms',
              '&.Mui-checked': {
                transform: 'translateX(16px)',
                color: '#fff',
                '& + .MuiSwitch-track': {
                  backgroundColor: tokens.primary.main,
                  opacity: 1,
                  border: 0,
                },
                '&.Mui-disabled + .MuiSwitch-track': {
                  opacity: 0.5,
                },
              },
              '&.Mui-focusVisible .MuiSwitch-thumb': {
                color: tokens.primary.main,
                border: '6px solid #fff',
              },
              '&.Mui-disabled .MuiSwitch-thumb': {
                color: mode === 'light' ? '#e9e9ea' : '#39393d',
              },
              '&.Mui-disabled + .MuiSwitch-track': {
                opacity: mode === 'light' ? 0.7 : 0.3,
              },
            },
            '& .MuiSwitch-thumb': {
              boxSizing: 'border-box',
              width: 20,
              height: 20,
              boxShadow: '0 2px 4px 0 rgba(0,0,0,0.2)',
            },
            '& .MuiSwitch-track': {
              borderRadius: 26 / 2,
              backgroundColor: mode === 'light' ? '#e9e9ea' : 'rgba(255,255,255,0.2)',
              opacity: 1,
              transition: 'background-color 300ms cubic-bezier(0.4, 0, 0.2, 1)',
            },
          },
          sizeSmall: {
            width: 36,
            height: 22,
            '& .MuiSwitch-switchBase': {
              margin: 3,
              '&.Mui-checked': {
                transform: 'translateX(14px)',
              },
            },
            '& .MuiSwitch-thumb': {
              width: 16,
              height: 16,
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            '&:hover': {
              backgroundColor: alpha(tokens.primary.main, 0.08),
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: alpha(tokens.primary.main, 0.08),
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 48,
          },
          indicator: {
            height: 3,
            borderRadius: '3px 3px 0 0',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
            minHeight: 48,
            padding: '12px 16px',
            '&:hover': {
              backgroundColor: tokens.action.hover,
            },
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            borderRadius: 12, // Down from 20px (cleaner)
            '&.Mui-selected': {
              backgroundColor: alpha(tokens.primary.main, 0.12),
              '&:hover': {
                backgroundColor: alpha(tokens.primary.main, 0.16),
              },
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 10, // Reduced from 12px
            padding: '12px 16px', // Up from 8px 12px (more spacious)
            marginBottom: '4px',
            transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: tokens.action.hover,
            },
            '&.Mui-selected': {
              backgroundColor: alpha(tokens.primary.main, 0.12),
              color: tokens.primary.main,
              fontWeight: 600, // Bolder when selected
              '&:hover': {
                backgroundColor: alpha(tokens.primary.main, 0.16),
              },
              '& .MuiListItemIcon-root': {
                color: tokens.primary.main, // Icon color matches
              },
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: 40,
            color: tokens.text.secondary,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 8, // Reduced
            boxShadow: '0px 6px 16px rgba(91, 79, 204, 0.12), 0px 3px 6px rgba(91, 79, 204, 0.08)',
            border: 'none',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            margin: '2px 8px',
            padding: '8px 12px',
            '&:hover': {
              backgroundColor: tokens.action.hover,
            },
            '&.Mui-selected': {
              backgroundColor: alpha(tokens.primary.main, 0.12),
              '&:hover': {
                backgroundColor: alpha(tokens.primary.main, 0.16),
              },
            },
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: 8, // Reduced
            boxShadow: '0px 6px 16px rgba(91, 79, 204, 0.12), 0px 3px 6px rgba(91, 79, 204, 0.08)',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16, // Reduced from 20px
            boxShadow:
              mode === 'light'
                ? '0px 10px 30px rgba(91, 79, 204, 0.15), 0px 5px 12px rgba(91, 79, 204, 0.10)'
                : '0px 22px 60px rgba(0,0,0,0.6)',
            backgroundColor: mode === 'light' ? tokens.surface.main : tokens.surface.elevated,
            border: mode === 'light' ? 'none' : `1px solid ${alpha(tokens.divider, 0.5)}`,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: '1.1rem',
            fontWeight: 600,
            padding: '18px 20px 12px',
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '14px 20px',
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '12px 20px 16px',
            gap: 6,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: mode === 'light' ? '#3c4043' : '#e8eaed',
            color: mode === 'light' ? '#ffffff' : '#202124',
            fontSize: '0.75rem',
            fontWeight: 500,
            padding: '8px 12px',
            borderRadius: 4,
          },
          arrow: {
            color: mode === 'light' ? '#3c4043' : '#e8eaed',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '10px 14px',
          },
          standardSuccess: {
            backgroundColor: alpha(tokens.success.main, 0.12),
            color: tokens.success.dark,
          },
          standardError: {
            backgroundColor: alpha(tokens.error.main, 0.12),
            color: tokens.error.dark,
          },
          standardWarning: {
            backgroundColor: alpha(tokens.warning.main, 0.12),
            color: tokens.warning.dark,
          },
          standardInfo: {
            backgroundColor: alpha(tokens.info.main, 0.12),
            color: tokens.info.dark,
          },
        },
      },
      MuiSnackbar: {
        styleOverrides: {
          root: {
            '& .MuiSnackbarContent-root': {
              borderRadius: 8,
            },
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          badge: {
            fontWeight: 500,
            fontSize: '0.6875rem',
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            fontWeight: 500,
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
          rounded: {
            borderRadius: 8,
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            height: 4,
          },
        },
      },
      MuiCircularProgress: {
        styleOverrides: {
          root: {
            color: tokens.primary.main,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: tokens.divider,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-root': {
              fontWeight: 600, // Up from 500 (bolder)
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontSize: '0.82rem',
              backgroundColor: tokens.surface.variant,
              borderBottom: `1px solid ${tokens.divider}`,
              padding: '12px 16px',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${tokens.divider}`,
            padding: '12px 16px',
            fontSize: '0.9rem',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: tokens.action.hover,
            },
          },
        },
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          separator: {
            color: tokens.text.secondary,
          },
        },
      },
      MuiAccordion: {
        defaultProps: {
          elevation: 1,
        },
        styleOverrides: {
          root: {
            borderRadius: 10, // Reduced
            border: 'none',
            boxShadow: '0px 1px 3px rgba(91, 79, 204, 0.08), 0px 1px 2px rgba(91, 79, 204, 0.06)',
            '&:before': {
              display: 'none',
            },
            '&.Mui-expanded': {
              margin: 0,
            },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            '&:hover': {
              backgroundColor: tokens.action.hover,
            },
          },
        },
      },
    },
  });
};
